import { auth } from '../firebaseConfig';

export function parseInvitationToken(value) {
  const raw = String(value || '').trim();
  if (/^[a-f0-9]{48}$/.test(raw)) return raw;
  try {
    const url = new URL(raw);
    if (!['http:', 'https:'].includes(url.protocol)) return '';
    const token = url.pathname.match(/^\/student\/join\/([a-f0-9]{48})\/?$/)?.[1];
    return token || '';
  } catch (_) {
    return '';
  }
}

export async function invitationApi(action, body = {}, publicRequest = false) {
  const configured = process.env.REACT_APP_API_BASE_URL;
  const local = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const base = String(
    configured ||
      (local ? 'https://us-central1-houari-boumedienne-schoo-4d4c2.cloudfunctions.net' : '')
  ).replace(/\/+$/, '');
  const headers = { 'Content-Type': 'application/json' };
  if (!publicRequest) {
    if (!auth.currentUser)
      throw Object.assign(new Error('sign-in-required'), { code: 'sign-in-required' });
    headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
  }
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(`${base}/api/class-invitations/${action}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const result = await response.json().catch((error) => {
      if (error.name === 'AbortError') throw error;
      return null;
    });
    if (response.ok && result?.ok === true) return result;

    // Keep backend validation errors, but distinguish a missing API/HTML
    // fallback from an actual invitation error (which also may return 404).
    const code =
      typeof result?.code === 'string'
        ? result.code
        : response.status === 401
          ? 'sign-in-required'
          : response.status === 403
            ? 'invitation-service-forbidden'
            : [404, 405].includes(response.status) || response.ok
              ? 'invitation-service-not-deployed'
              : response.status >= 500
                ? 'invitation-service-unavailable'
                : 'invitation-error';
    throw Object.assign(new Error(code), { code, status: response.status });
  } catch (error) {
    if (typeof error.code === 'string') throw error;
    const code =
      error.name === 'AbortError' ? 'invitation-service-timeout' : 'invitation-service-unavailable';
    throw Object.assign(new Error(code), { code });
  } finally {
    clearTimeout(timeout);
  }
}

export function invitationError(error, t) {
  const messages = {
    'invitation-service-unavailable':
      'The invitation service cannot be reached. Please try again or contact your school.',
    'invitation-service-timeout':
      'The invitation service took too long to respond. Please try again.',
    'invitation-service-not-deployed':
      'Invitations are not available yet. Your school needs to finish setting up this feature.',
    'invitation-service-forbidden':
      'Access to the invitation service is blocked. Please contact your school.',
    'invitations-not-configured':
      'Class invitations are not enabled yet. Please contact the school.',
    'invalid-invitation': 'This invitation is invalid. Ask your teacher for a new link.',
    'invitation-disabled': 'This invitation has been disabled. Ask your teacher for a new link.',
    'invitation-expired': 'This invitation has expired. Ask your teacher for a new link.',
    'class-not-found': 'This class is no longer available.',
    'not-class-owner': 'Only the teacher of this class can manage invitations.',
    'sign-in-required': 'Please sign in again to continue.',
    'account-restricted':
      'Your account is restricted or your membership is inactive. Please contact your teacher.',
    'account-conflict': 'An existing student record needs attention. Please contact your teacher.',
    'use-student-account': 'Please use a student account to join this class.',
    'verify-email': 'Please verify your email before linking your existing student record.',
    'name-required': 'Please enter your first and last name.',
    'request-rejected': 'Your request was declined. Please contact your teacher.',
    'auth/email-already-in-use':
      'This email already has an account. Sign in instead, or reset your password.',
    'auth/invalid-credential': 'The email or password is incorrect.',
    'auth/wrong-password': 'The email or password is incorrect.',
    'auth/user-not-found': 'The email or password is incorrect.',
    'auth/weak-password': 'Please choose a stronger password with at least 6 characters.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/too-many-requests': 'Too many attempts. Please wait and try again.',
    'auth/network-request-failed': 'Please check your connection and try again.',
  };
  return t(messages[error?.code] || 'Unable to complete this invitation action. Please try again.');
}
